import { PageSettingsPatch } from '@storefronts/common-types'
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { v4 as uuid } from 'uuid'
import { SectionWithHash } from '../editorblocks/sectionWithHash'
import { summarizeSectionsDiff } from '../utils/summarizeSectionsDiff'
import {
    snapshotPageSettings,
    summarizePageSettingsDiff,
} from '../utils/summarizePageSettingsDiff'

const summarizeHistoryDiff = (
    previousSections: SectionWithHash[],
    nextSections: SectionWithHash[],
    previousPageSettings: PageSettingsPatch,
    nextPageSettings: PageSettingsPatch,
): string => {
    const parts = [
        summarizeSectionsDiff(previousSections, nextSections),
        summarizePageSettingsDiff(previousPageSettings, nextPageSettings),
    ].filter((part) => part !== 'No changes')
    return parts.length ? parts.join('; ') : 'No changes'
}

const hasMeaningfulChange = (
    previousSections: SectionWithHash[],
    nextSections: SectionWithHash[],
    previousPageSettings: PageSettingsPatch,
    nextPageSettings: PageSettingsPatch,
): boolean =>
    summarizeHistoryDiff(
        previousSections,
        nextSections,
        previousPageSettings,
        nextPageSettings,
    ) !== 'No changes'

const MAX_HISTORY_ENTRIES = 50
const MANUAL_COMMIT_DEBOUNCE_MS = 900

export type HistoryEntryOrigin =
    | { kind: 'manual' }
    | { kind: 'chat'; turnId: string; turnIndex: number; summary?: string }

export type HistoryEntry = {
    id: string
    sections: SectionWithHash[]
    pageSettings: PageSettingsPatch
    origin: HistoryEntryOrigin
    timestamp: number
    diffSummary: string
}

export type StagedContentHistory = {
    entries: HistoryEntry[]
    cursor: number
    canUndo: boolean
    canRedo: boolean
    undo: () => void
    redo: () => void
    revertTo: (entryId: string) => void
    commitChatTurn: (
        origin: { turnId: string; turnIndex: number; summary?: string },
        sections: SectionWithHash[],
        pageSettings?: PageSettingsPatch,
    ) => void
    resetHistory: (sections?: SectionWithHash[]) => void
}

const makeInitialEntry = (
    sections: SectionWithHash[],
    pageSettings: PageSettingsPatch,
): HistoryEntry => ({
    id: uuid(),
    sections: structuredClone(sections),
    pageSettings: structuredClone(pageSettings),
    origin: { kind: 'manual' },
    timestamp: Date.now(),
    diffSummary: 'Initial version',
})

/**
 * Tracks an undo/redo timeline for staged section and page-settings edits,
 * independent of how those edits are made (manual editor blocks, settings
 * panel, or a chat-bot orchestrator). Snapshots are structuredClone'd on push
 * and on restore so the stack's correctness never depends on mutation call
 * sites treating staged state as immutable.
 */
export function useStagedContentHistory(
    sections: SectionWithHash[],
    setSections: Dispatch<SetStateAction<SectionWithHash[]>>,
    onSectionsRestored?: (sections: SectionWithHash[]) => void,
    pageSettings: PageSettingsPatch = {},
    onPageSettingsRestored?: (pageSettings: PageSettingsPatch) => void,
): StagedContentHistory {
    const normalizedPageSettings = snapshotPageSettings(pageSettings)
    const [entries, setEntries] = useState<HistoryEntry[]>(() => [
        makeInitialEntry(sections, pageSettings),
    ])
    const [cursor, setCursor] = useState(0)

    const entriesRef = useRef(entries)
    entriesRef.current = entries
    const cursorRef = useRef(cursor)
    cursorRef.current = cursor
    const sectionsRef = useRef(sections)
    sectionsRef.current = sections
    const pageSettingsRef = useRef(pageSettings)
    pageSettingsRef.current = pageSettings
    const isRestoringRef = useRef(false)
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>()

    const clearPendingCommit = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = undefined
        }
    }, [])

    const pushEntry = useCallback(
        (
            newSections: SectionWithHash[],
            newPageSettings: PageSettingsPatch,
            origin: HistoryEntryOrigin,
        ) => {
            const currentEntries = entriesRef.current
            const currentCursor = cursorRef.current
            const previous = currentEntries[currentCursor]
            const previousSections = previous?.sections ?? []
            const previousPageSettings = previous?.pageSettings ?? {}
            const diffSummary = summarizeHistoryDiff(
                previousSections,
                newSections,
                previousPageSettings,
                newPageSettings,
            )
            // Nothing actually changed since the last checkpoint (e.g. the debounce
            // effect firing once on mount, before any real edit) — skip creating a
            // no-op entry, which would otherwise falsely enable Undo and can clobber
            // a real forward (redo) branch via the truncation below.
            if (diffSummary === 'No changes') {
                return
            }
            const entry: HistoryEntry = {
                id: uuid(),
                sections: structuredClone(newSections),
                pageSettings: structuredClone(newPageSettings),
                origin,
                timestamp: Date.now(),
                diffSummary,
            }
            const truncated = currentEntries.slice(0, currentCursor + 1)
            const next = [...truncated, entry].slice(-MAX_HISTORY_ENTRIES)
            setEntries(next)
            setCursor(next.length - 1)
        },
        [],
    )

    // Debounce-coalesce bursts of manual edits (e.g. typing) into a single history entry.
    // pageSettingsKey keeps object-identity churn from resetting the timer every render.
    const pageSettingsKey = JSON.stringify(normalizedPageSettings)
    useEffect(() => {
        if (isRestoringRef.current) {
            isRestoringRef.current = false
            return
        }
        const timer = setTimeout(() => {
            pushEntry(sections, pageSettingsRef.current, { kind: 'manual' })
        }, MANUAL_COMMIT_DEBOUNCE_MS)
        debounceTimerRef.current = timer
        return () => clearTimeout(timer)
    }, [sections, pageSettingsKey, pushEntry])

    const restoreSnapshot = useCallback(
        (
            targetSections: SectionWithHash[],
            targetPageSettings: PageSettingsPatch,
        ) => {
            isRestoringRef.current = true
            const clonedSections = structuredClone(targetSections)
            const clonedPageSettings = structuredClone(targetPageSettings)
            setSections(clonedSections)
            onSectionsRestored?.(clonedSections)
            onPageSettingsRestored?.(clonedPageSettings)
        },
        [onPageSettingsRestored, onSectionsRestored, setSections],
    )

    const undo = useCallback(() => {
        const currentEntries = entriesRef.current
        const currentCursor = cursorRef.current
        const currentEntry = currentEntries[currentCursor]
        const currentEntrySections = currentEntry?.sections ?? []
        const currentEntryPageSettings = currentEntry?.pageSettings ?? {}
        const hasUncommittedEdit = hasMeaningfulChange(
            currentEntrySections,
            sectionsRef.current,
            currentEntryPageSettings,
            pageSettingsRef.current,
        )

        // The live content already diverged from the last checkpoint (e.g. a manual
        // edit still sitting in the debounce window). Discard just that pending edit
        // and land back on the last real checkpoint, rather than also skipping past
        // it to cursor - 1.
        if (hasUncommittedEdit) {
            clearPendingCommit()
            restoreSnapshot(currentEntrySections, currentEntryPageSettings)
            return
        }

        if (currentCursor <= 0) return
        clearPendingCommit()
        const targetCursor = currentCursor - 1
        const target = currentEntries[targetCursor]
        setCursor(targetCursor)
        restoreSnapshot(target.sections, target.pageSettings)
    }, [clearPendingCommit, restoreSnapshot])

    const redo = useCallback(() => {
        const currentEntries = entriesRef.current
        const currentCursor = cursorRef.current
        const currentEntry = currentEntries[currentCursor]
        const currentEntrySections = currentEntry?.sections ?? []
        const currentEntryPageSettings = currentEntry?.pageSettings ?? {}
        const hasUncommittedEdit = hasMeaningfulChange(
            currentEntrySections,
            sectionsRef.current,
            currentEntryPageSettings,
            pageSettingsRef.current,
        )
        // A live uncommitted edit has no well-defined "forward" state to redo into.
        if (hasUncommittedEdit) return
        if (currentCursor >= currentEntries.length - 1) return
        clearPendingCommit()
        const targetCursor = currentCursor + 1
        const target = currentEntries[targetCursor]
        setCursor(targetCursor)
        restoreSnapshot(target.sections, target.pageSettings)
    }, [clearPendingCommit, restoreSnapshot])

    const revertTo = useCallback(
        (entryId: string) => {
            const currentEntries = entriesRef.current
            const targetIndex = currentEntries.findIndex(
                (e) => e.id === entryId,
            )
            if (targetIndex === -1 || targetIndex === cursorRef.current) return
            clearPendingCommit()
            const target = currentEntries[targetIndex]
            setCursor(targetIndex)
            restoreSnapshot(target.sections, target.pageSettings)
        },
        [clearPendingCommit, restoreSnapshot],
    )

    const commitChatTurn = useCallback(
        (
            origin: { turnId: string; turnIndex: number; summary?: string },
            newSections: SectionWithHash[],
            newPageSettings?: PageSettingsPatch,
        ) => {
            clearPendingCommit()
            const nextPageSettings = newPageSettings ?? pageSettingsRef.current
            // Apply first so the editor reflects the turn even when the history
            // push is a no-op (unchanged sections and page settings).
            restoreSnapshot(newSections, nextPageSettings)
            pushEntry(newSections, nextPageSettings, {
                kind: 'chat',
                ...origin,
            })
        },
        [clearPendingCommit, pushEntry, restoreSnapshot],
    )

    const resetHistory = useCallback(
        (newSections?: SectionWithHash[]) => {
            clearPendingCommit()
            const entry = makeInitialEntry(
                newSections ?? sectionsRef.current,
                pageSettingsRef.current,
            )
            setEntries([entry])
            setCursor(0)
        },
        [clearPendingCommit],
    )

    // Mirrors the pending-edit check inside undo()/redo(), computed from render-time
    // state so the buttons reflect it without waiting for the debounce to commit.
    const hasUncommittedEdit = useMemo(() => {
        const currentEntry = entries[cursor]
        return hasMeaningfulChange(
            currentEntry?.sections ?? [],
            sections,
            currentEntry?.pageSettings ?? {},
            pageSettings,
        )
    }, [sections, pageSettings, entries, cursor])

    return {
        entries,
        cursor,
        canUndo: cursor > 0 || hasUncommittedEdit,
        canRedo: cursor < entries.length - 1 && !hasUncommittedEdit,
        undo,
        redo,
        revertTo,
        commitChatTurn,
        resetHistory,
    }
}
